/**
 * SLS Analytics
 * Specific analytics helpers for Store Location Services
 *
 * Implements metrics outlined in SLS Telemetry requirements:
 * - 3.1 Adoption & UI Usage Metrics
 * - 3.2 API Usage Metrics
 * - 3.3 Store Hours Ownership Metrics
 * - 3.4 Store Lifecycle Metrics
 * - 3.5 Data Quality & Reliability Metrics
 *
 * IMPORTANT: All analytics functions are wrapped in try-catch to ensure
 * they NEVER impact main application functionality if they fail.
 */

import { sendNewRelicEvent, sendNewRelicError } from '../newrelic';

import { FEATURES, SLS_EVENTS } from './constants';
import { trackFeatureUsage } from './sessionTracker';

// Store edit start times for duration calculation
const editStartTimes = new Map();

/**
 * Safe wrapper for analytics calls - ensures errors never propagate
 * @param {Function} fn - Analytics function to execute
 * @param {string} fnName - Function name for debugging
 */
const safeAnalyticsCall = (fn, fnName) => {
  try {
    fn();
  } catch (error) {
    // Silently fail - analytics should never break the app
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[SLS Analytics] ${fnName} failed:`, error);
    }
  }
};

/**
 * Safe wrapper for trackFeatureUsage - ensures errors never propagate
 */
const safeTrackFeatureUsage = (featureName, action, context = {}) => {
  try {
    trackFeatureUsage(featureName, action, context);
  } catch (error) {
    // Silently fail
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SLS Analytics] trackFeatureUsage failed:', error);
    }
  }
};

// ============ 3.1 Adoption & UI Usage Metrics ============

/**
 * Track SLS page viewed
 * Supports: Monthly Active Users (UI), Daily Active Users (UI), User Role Breakdown, Region Breakdown
 * @param {object} params - Page view parameters
 */
// Helper to get feature name from facility type
const getFeatureNameFromFacilityType = (facilityType) => {
  if (facilityType === 'digital-stores') return FEATURES.SLS_DIGITAL_STORES;
  if (facilityType === 'warehouses') return FEATURES.SLS_WAREHOUSES;
  return FEATURES.SLS_STORES;
};

export const trackSlsPageViewed = ({
  userGroups = [],
  userEmail = null,
  region = null,
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    const featureName = getFeatureNameFromFacilityType(facilityType);

    safeTrackFeatureUsage(featureName, 'pageViewed');
    sendNewRelicEvent(SLS_EVENTS.PAGE_VIEWED, {
      facilityType,
      featureName,
      region,
      timestamp: new Date().toISOString(),
      userEmail,
      userGroups,
    });
  }, 'trackSlsPageViewed');
};

/**
 * Track facility type switch (Stores/Digital Stores/Warehouses)
 * @param {object} params - Facility type parameters
 */
export const trackSlsFacilityTypeSwitched = ({
  fromType,
  toType,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.FACILITY_TYPE_SWITCHED, {
      featureName: FEATURES.SLS,
      fromType,
      timestamp: new Date().toISOString(),
      toType,
      userEmail,
    });
  }, 'trackSlsFacilityTypeSwitched');
};

/**
 * Track region filter selection
 * Supports: Region Breakdown - Users by geo (NA / EMEA / APLA / GC)
 * @param {object} params - Region selection parameters
 */
export const trackSlsRegionFiltered = ({
  region,
  previousRegion = null,
  resultsCount = 0,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.REGION_FILTERED, {
      featureName: FEATURES.SLS_STORES,
      previousRegion,
      region,
      resultsCount,
      userEmail,
    });
  }, 'trackSlsRegionFiltered');
};

/**
 * Track store selection from list
 * Supports: Power Users - Users performing >= X actions/week
 * @param {object} params - Store selection parameters
 */
export const trackSlsStoreSelected = ({
  storeId,
  storeNumber,
  region,
  facilityType = 'stores',
  country = null,
}) => {
  safeAnalyticsCall(() => {
    const featureName = getFeatureNameFromFacilityType(facilityType);

    safeTrackFeatureUsage(featureName, 'storeSelected', { storeId });
    sendNewRelicEvent(SLS_EVENTS.STORE_SELECTED, {
      country,
      facilityType,
      featureName,
      region,
      storeId,
      storeNumber,
    });
  }, 'trackSlsStoreSelected');
};

/**
 * Track search performed
 * @param {object} params - Search parameters
 */
export const trackSlsSearchPerformed = ({
  searchTerm,
  resultsCount,
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.SEARCH_PERFORMED, {
      facilityType,
      featureName: FEATURES.SLS,
      resultsCount,
      searchTerm: searchTerm?.substring(0, 100),
    });
  }, 'trackSlsSearchPerformed');
};

// ============ 3.2 API Usage Metrics ============

/**
 * Track API call made
 * Supports: Public API Calls, Private API Calls, Read vs Write Ratio
 * @param {object} params - API call parameters
 */
export const trackSlsApiCall = ({
  endpoint,
  method,
  isPublic = true,
  consumer = 'SLS_UI',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.API_CALL_MADE, {
      consumer,
      endpoint,
      featureName: FEATURES.SLS,
      isPublic,
      method,
      timestamp: new Date().toISOString(),
    });
  }, 'trackSlsApiCall');
};

/**
 * Track API call success
 * @param {object} params - Success parameters
 */
export const trackSlsApiSuccess = ({
  endpoint,
  method,
  durationMs,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.API_CALL_SUCCESS, {
      durationMs,
      endpoint,
      featureName: FEATURES.SLS,
      method,
      success: true,
    });
  }, 'trackSlsApiSuccess');
};

/**
 * Track API latency
 * Supports: API Response Time - P95 latency <300ms
 * @param {object} params - Latency parameters
 */
export const trackSlsApiLatency = ({
  endpoint,
  method,
  durationMs,
  success,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.API_LATENCY, {
      durationMs,
      endpoint,
      featureName: FEATURES.SLS,
      method,
      success,
    });
  }, 'trackSlsApiLatency');
};

/**
 * Track API call error
 * Supports: Failed Integration Count
 * @param {object} params - Error parameters
 */
export const trackSlsApiError = ({
  endpoint,
  method,
  statusCode,
  errorMessage,
  errorType = 'unknown',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.API_CALL_ERROR, {
      endpoint,
      errorMessage: errorMessage?.substring(0, 500),
      errorType,
      featureName: FEATURES.SLS,
      method,
      statusCode,
    });
    sendNewRelicError(new Error(errorMessage || 'Unknown error'), {
      endpoint,
      feature: FEATURES.SLS,
      method,
      operation: 'apiCall',
      statusCode,
    });
  }, 'trackSlsApiError');
};

// ============ 3.3 Store Hours Ownership Metrics ============

/**
 * Track store hours updated
 * Supports: Store Hours Updated via SLS, Unique Users Updating Store Hours
 * @param {object} params - Hours update parameters
 */
export const trackSlsStoreHoursUpdated = ({
  storeId,
  storeNumber,
  userEmail = null,
  hoursType,
  region = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_STORES, 'storeHoursUpdated', { storeId });
    sendNewRelicEvent(SLS_EVENTS.STORE_HOURS_UPDATED, {
      featureName: FEATURES.SLS_STORES,
      hoursType,
      region,
      storeId,
      storeNumber,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsStoreHoursUpdated');
};

/**
 * Track regular hours modification
 * @param {object} params - Regular hours parameters
 */
export const trackSlsRegularHoursModified = ({
  storeId,
  storeNumber,
  dayOfWeek,
  openTime,
  closeTime,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.REGULAR_HOURS_MODIFIED, {
      closeTime,
      dayOfWeek,
      featureName: FEATURES.SLS_STORES,
      openTime,
      storeId,
      storeNumber,
      userEmail,
    });
  }, 'trackSlsRegularHoursModified');
};

/**
 * Track special hours added
 * @param {object} params - Special hours parameters
 */
export const trackSlsSpecialHoursAdded = ({
  storeId,
  storeNumber,
  date,
  reason = null,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.SPECIAL_HOURS_ADDED, {
      date,
      featureName: FEATURES.SLS_STORES,
      reason,
      storeId,
      storeNumber,
      userEmail,
    });
  }, 'trackSlsSpecialHoursAdded');
};

/**
 * Track special hours removed
 * @param {object} params - Special hours removal parameters
 */
export const trackSlsSpecialHoursRemoved = ({
  storeId,
  storeNumber,
  date,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.SPECIAL_HOURS_REMOVED, {
      date,
      featureName: FEATURES.SLS_STORES,
      storeId,
      storeNumber,
      userEmail,
    });
  }, 'trackSlsSpecialHoursRemoved');
};

// ============ 3.4 Store Lifecycle Metrics ============

/**
 * Track store created
 * Supports: New Stores Created - Number of new stores created in SLS
 * @param {object} params - Store creation parameters
 */
export const trackSlsStoreCreated = ({
  storeId,
  storeNumber,
  region,
  country,
  facilityType,
  businessConcept = null,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_STORES, 'storeCreated', { region, storeId });
    sendNewRelicEvent(SLS_EVENTS.STORE_CREATED, {
      businessConcept,
      country,
      facilityType,
      featureName: FEATURES.SLS_STORES,
      region,
      storeId,
      storeNumber,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsStoreCreated');
};

/**
 * Track store updated
 * Supports: Existing Stores Updated - Stores with >=1 field updated
 * @param {object} params - Store update parameters
 */
export const trackSlsStoreUpdated = ({
  storeId,
  storeNumber,
  region,
  fieldsUpdatedCount = 0,
  fieldsUpdated = [],
  userEmail = null,
  durationMs = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_STORES, 'storeUpdated', { fieldsUpdatedCount, storeId });
    sendNewRelicEvent(SLS_EVENTS.STORE_UPDATED, {
      durationMs,
      featureName: FEATURES.SLS_STORES,
      fieldsUpdated: Array.isArray(fieldsUpdated) ? fieldsUpdated.slice(0, 20) : [],
      fieldsUpdatedCount,
      region,
      storeId,
      storeNumber,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsStoreUpdated');
};

/**
 * Track individual field modification
 * Supports: Fields Updated per Store, Top Updated Fields
 * @param {object} params - Field modification parameters
 */
export const trackSlsFieldModified = ({
  storeId,
  fieldName,
  fieldSection,
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.STORE_FIELD_MODIFIED, {
      facilityType,
      featureName: FEATURES.SLS,
      fieldName,
      fieldSection,
      storeId,
    });
  }, 'trackSlsFieldModified');
};

/**
 * Track bulk create started
 * @param {object} params - Bulk create parameters
 */
export const trackSlsBulkCreateStarted = ({
  storesCount,
  region,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_STORES, 'bulkCreateStarted');
    sendNewRelicEvent(SLS_EVENTS.BULK_CREATE_STARTED, {
      featureName: FEATURES.SLS_STORES,
      region,
      storesCount,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsBulkCreateStarted');
};

/**
 * Track bulk create completed
 * @param {object} params - Bulk create completion parameters
 */
export const trackSlsBulkCreateCompleted = ({
  successCount,
  failedCount,
  totalCount,
  region,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.BULK_CREATE_COMPLETED, {
      failedCount,
      featureName: FEATURES.SLS_STORES,
      region,
      successCount,
      successRate: totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0,
      timestamp: new Date().toISOString(),
      totalCount,
      userEmail,
    });
  }, 'trackSlsBulkCreateCompleted');
};

/**
 * Track bulk edit started
 * @param {object} params - Bulk edit parameters
 */
export const trackSlsBulkEditStarted = ({
  storesCount,
  region,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_STORES, 'bulkEditStarted');
    sendNewRelicEvent(SLS_EVENTS.BULK_EDIT_STARTED, {
      featureName: FEATURES.SLS_STORES,
      region,
      storesCount,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsBulkEditStarted');
};

/**
 * Track bulk edit completed
 * @param {object} params - Bulk edit completion parameters
 */
export const trackSlsBulkEditCompleted = ({
  successCount,
  failedCount,
  totalCount,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.BULK_EDIT_COMPLETED, {
      failedCount,
      featureName: FEATURES.SLS_STORES,
      successCount,
      successRate: totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0,
      timestamp: new Date().toISOString(),
      totalCount,
      userEmail,
    });
  }, 'trackSlsBulkEditCompleted');
};

/**
 * Track digital store created
 * @param {object} params - Digital store creation parameters
 */
export const trackSlsDigitalStoreCreated = ({
  storeId,
  storeNumber,
  region,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_DIGITAL_STORES, 'storeCreated', { storeId });
    sendNewRelicEvent(SLS_EVENTS.DIGITAL_STORE_CREATED, {
      featureName: FEATURES.SLS_DIGITAL_STORES,
      region,
      storeId,
      storeNumber,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsDigitalStoreCreated');
};

/**
 * Track digital store updated
 * @param {object} params - Digital store update parameters
 */
export const trackSlsDigitalStoreUpdated = ({
  storeId,
  storeNumber,
  fieldsUpdatedCount = 0,
  userEmail = null,
  durationMs = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_DIGITAL_STORES, 'storeUpdated', { storeId });
    sendNewRelicEvent(SLS_EVENTS.DIGITAL_STORE_UPDATED, {
      durationMs,
      featureName: FEATURES.SLS_DIGITAL_STORES,
      fieldsUpdatedCount,
      storeId,
      storeNumber,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackSlsDigitalStoreUpdated');
};

/**
 * Track warehouse created
 * @param {object} params - Warehouse creation parameters
 */
export const trackSlsWarehouseCreated = ({
  warehouseId,
  warehouseNumber,
  region,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_WAREHOUSES, 'warehouseCreated', { warehouseId });
    sendNewRelicEvent(SLS_EVENTS.WAREHOUSE_CREATED, {
      featureName: FEATURES.SLS_WAREHOUSES,
      region,
      timestamp: new Date().toISOString(),
      userEmail,
      warehouseId,
      warehouseNumber,
    });
  }, 'trackSlsWarehouseCreated');
};

/**
 * Track warehouse updated
 * @param {object} params - Warehouse update parameters
 */
export const trackSlsWarehouseUpdated = ({
  warehouseId,
  warehouseNumber,
  fieldsUpdatedCount = 0,
  userEmail = null,
  durationMs = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SLS_WAREHOUSES, 'warehouseUpdated', { warehouseId });
    sendNewRelicEvent(SLS_EVENTS.WAREHOUSE_UPDATED, {
      durationMs,
      featureName: FEATURES.SLS_WAREHOUSES,
      fieldsUpdatedCount,
      timestamp: new Date().toISOString(),
      userEmail,
      warehouseId,
      warehouseNumber,
    });
  }, 'trackSlsWarehouseUpdated');
};

// ============ 3.5 Data Quality & Reliability Metrics ============

/**
 * Track validation error
 * Supports: Validation Errors - Field-level validation failures
 * @param {object} params - Validation error parameters
 */
export const trackSlsValidationError = ({
  storeId = null,
  fieldName,
  errorMessage,
  formSection,
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.VALIDATION_ERROR, {
      errorMessage: errorMessage?.substring(0, 200),
      facilityType,
      featureName: FEATURES.SLS,
      fieldName,
      formSection,
      storeId,
    });
  }, 'trackSlsValidationError');
};

/**
 * Track mandatory field missing
 * Supports: Mandatory Field Completion - % of stores with all required fields
 * @param {object} params - Missing field parameters
 */
export const trackSlsMandatoryFieldMissing = ({
  storeId,
  fieldName,
  formSection,
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.MANDATORY_FIELD_MISSING, {
      facilityType,
      featureName: FEATURES.SLS,
      fieldName,
      formSection,
      storeId,
    });
  }, 'trackSlsMandatoryFieldMissing');
};

/**
 * Track form error displayed to user
 * Supports: User Trust / Experience - % of sessions with errors
 * @param {object} params - Form error parameters
 */
export const trackSlsFormErrorDisplayed = ({
  storeId = null,
  errorCount,
  errorFields = [],
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.FORM_ERROR_DISPLAYED, {
      errorCount,
      errorFields: Array.isArray(errorFields) ? errorFields.slice(0, 20) : [],
      facilityType,
      featureName: FEATURES.SLS,
      storeId,
    });
  }, 'trackSlsFormErrorDisplayed');
};

/**
 * Track update failed
 * Supports: Failed Updates - Number of rejected or failed updates
 * @param {object} params - Update failure parameters
 */
export const trackSlsUpdateFailed = ({
  storeId,
  errorMessage,
  errorCode = null,
  errorType = 'unknown',
  facilityType = 'stores',
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.UPDATE_FAILED, {
      errorCode,
      errorMessage: errorMessage?.substring(0, 500),
      errorType,
      facilityType,
      featureName: FEATURES.SLS,
      storeId,
      userEmail,
    });
    sendNewRelicError(new Error(errorMessage || 'Unknown error'), {
      errorCode,
      facility: storeId,
      feature: FEATURES.SLS,
      operation: 'storeUpdate',
    });
  }, 'trackSlsUpdateFailed');
};

/**
 * Track update corrected (re-update within X hours)
 * Supports: Update Corrections - Updates overwritten within X hrs
 * @param {object} params - Correction parameters
 */
export const trackSlsUpdateCorrected = ({
  storeId,
  fieldName,
  timeSinceLastUpdateMs,
  userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SLS_EVENTS.UPDATE_CORRECTED, {
      featureName: FEATURES.SLS,
      fieldName,
      storeId,
      timeSinceLastUpdateHours: timeSinceLastUpdateMs ? Math.round(timeSinceLastUpdateMs / (1000 * 60 * 60)) : 0,
      timeSinceLastUpdateMs,
      userEmail,
    });
  }, 'trackSlsUpdateCorrected');
};

// ============ Edit Duration & Session Tracking ============

/**
 * Track edit started - captures start time for duration calculation
 * @param {object} params - Edit start parameters
 */
export const trackSlsEditStarted = ({
  storeId,
  facilityType = 'stores',
}) => {
  safeAnalyticsCall(() => {
    if (storeId) {
      editStartTimes.set(storeId, Date.now());
    }
    sendNewRelicEvent(SLS_EVENTS.EDIT_STARTED, {
      facilityType,
      featureName: FEATURES.SLS,
      storeId,
      timestamp: new Date().toISOString(),
    });
  }, 'trackSlsEditStarted');
};

/**
 * Get edit duration and clean up
 * @param {string} storeId - Store ID
 * @returns {number|null} Duration in milliseconds or null
 */
export const getSlsEditDuration = (storeId) => {
  try {
    if (!storeId) return null;
    const startTime = editStartTimes.get(storeId);
    if (startTime) {
      const duration = Date.now() - startTime;
      editStartTimes.delete(storeId);
      return duration;
    }
    return null;
  } catch (error) {
    // Silently fail
    return null;
  }
};

/**
 * Track edit completed with duration
 * @param {object} params - Edit completion parameters
 */
export const trackSlsEditCompleted = ({
  storeId,
  facilityType = 'stores',
  success = true,
  durationMs = null,
}) => {
  safeAnalyticsCall(() => {
    const calculatedDuration = durationMs || getSlsEditDuration(storeId);
    sendNewRelicEvent(SLS_EVENTS.EDIT_COMPLETED, {
      durationMs: calculatedDuration,
      facilityType,
      featureName: FEATURES.SLS,
      storeId,
      success,
      timestamp: new Date().toISOString(),
    });
  }, 'trackSlsEditCompleted');
};

/**
 * Track form abandoned (user navigates away without saving)
 * Supports: User Trust / Experience metrics
 * @param {object} params - Form abandonment parameters
 */
export const trackSlsFormAbandoned = ({
  storeId = null,
  facilityType = 'stores',
  durationMs = null,
  hadChanges = false,
}) => {
  safeAnalyticsCall(() => {
    const calculatedDuration = durationMs || (storeId ? getSlsEditDuration(storeId) : null);
    sendNewRelicEvent(SLS_EVENTS.FORM_ABANDONED, {
      durationMs: calculatedDuration,
      facilityType,
      featureName: FEATURES.SLS,
      hadChanges,
      storeId,
    });
  }, 'trackSlsFormAbandoned');
};

// ============ Export Default ============

export default {
  getSlsEditDuration,
  trackSlsApiCall,
  trackSlsApiError,
  trackSlsApiLatency,
  trackSlsApiSuccess,
  trackSlsBulkCreateCompleted,
  trackSlsBulkCreateStarted,
  trackSlsBulkEditCompleted,
  trackSlsBulkEditStarted,
  trackSlsDigitalStoreCreated,
  trackSlsDigitalStoreUpdated,
  trackSlsEditCompleted,
  trackSlsEditStarted,
  trackSlsFacilityTypeSwitched,
  trackSlsFieldModified,
  trackSlsFormAbandoned,
  trackSlsFormErrorDisplayed,
  trackSlsMandatoryFieldMissing,
  trackSlsPageViewed,
  trackSlsRegionFiltered,
  trackSlsRegularHoursModified,
  trackSlsSearchPerformed,
  trackSlsSpecialHoursAdded,
  trackSlsSpecialHoursRemoved,
  trackSlsStoreCreated,
  trackSlsStoreHoursUpdated,
  trackSlsStoreSelected,
  trackSlsStoreUpdated,
  trackSlsUpdateCorrected,
  trackSlsUpdateFailed,
  trackSlsValidationError,
  trackSlsWarehouseCreated,
  trackSlsWarehouseUpdated,
};
